import type { GuidesReducerState } from './guidesReducer';

import { GuideId } from './guideId';
import { INITIAL_GUIDES_STATE } from './guidesReducer';

const STORAGE_KEY = 'guides';

/**
 * Type guard narrowing an arbitrary string key to a known GuideId.
 *
 * @param key - The key to check
 * @returns Whether the key is a valid GuideId member
 */
const isGuideId = (key: string): key is GuideId =>
    (Object.values(GuideId) as Array<string>).includes(key);

/**
 * Reads a persisted `Record<GuideId, T>` map, dropping keys that are no longer
 * a known GuideId and defaulting any missing guide to the initial value.
 *
 * @param persistedMap - The raw persisted map (unvalidated)
 * @param defaultMap - The map holding the default value for every valid guide
 * @returns A map containing exactly the current GuideId members
 */
const pruneGuideMap = <T extends boolean | number>(
    persistedMap: unknown,
    defaultMap: Record<GuideId, T>
): Record<GuideId, T> => {
    const prunedMap = { ...defaultMap };

    if (typeof persistedMap !== 'object' || persistedMap === null) {
        return prunedMap;
    }

    for (const [key, value] of Object.entries(persistedMap)) {
        if (!isGuideId(key)) continue;
        if (typeof value !== typeof defaultMap[key]) continue;

        prunedMap[key] = value as T;
    }

    return prunedMap;
};

/**
 * Reads and validates the persisted guides state from localStorage.
 *
 * Unlike the generic `isGetInitialStateFromStorage` path used by most slices
 * (which parses persisted JSON with no validation), this performs its own
 * safe parse so the app can never boot into a broken guide state:
 * - Malformed JSON, or a payload that isn't a plain object, falls back to the defaults.
 * - `completionByGuide` / `discoveryByGuide` / `stepIndexByGuide` are pruned to the
 *   current GuideId members, so removing a guide never leaves stale keys behind.
 * - The ephemeral fields `isManagerOpen` and `runningGuideId` are **never**
 *   restored. Hydrating them would mount the guide manager dialog (or replay a
 *   tour) on the very first render, only to close it again a tick later — that
 *   open/close race can leave MUI's Modal mounted with an invisible,
 *   full-viewport backdrop that swallows every click.
 *
 * @returns A pruned, type-safe guides state with ephemeral fields reset
 */
const getInitialGuidesState = (): GuidesReducerState => {
    let parsed: unknown;

    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return INITIAL_GUIDES_STATE;
        parsed = JSON.parse(stored);
    } catch {
        // Fail silently on malformed JSON — fall back to defaults
        return INITIAL_GUIDES_STATE;
    }

    if (
        typeof parsed !== 'object' ||
        parsed === null ||
        Array.isArray(parsed)
    ) {
        return INITIAL_GUIDES_STATE;
    }

    const persisted = parsed as Partial<
        Record<keyof GuidesReducerState, unknown>
    >;
    const { selectedManagerGuideId, isCelebrationOver } = persisted;

    return {
        // Ephemeral state is always reset — see the doc comment above
        isManagerOpen: false,
        runningGuideId: null,
        selectedManagerGuideId:
            typeof selectedManagerGuideId === 'string' &&
            isGuideId(selectedManagerGuideId)
                ? selectedManagerGuideId
                : INITIAL_GUIDES_STATE.selectedManagerGuideId,
        completionByGuide: pruneGuideMap<boolean>(
            persisted.completionByGuide,
            INITIAL_GUIDES_STATE.completionByGuide
        ),
        discoveryByGuide: pruneGuideMap<boolean>(
            persisted.discoveryByGuide,
            INITIAL_GUIDES_STATE.discoveryByGuide
        ),
        stepIndexByGuide: pruneGuideMap<number>(
            persisted.stepIndexByGuide,
            INITIAL_GUIDES_STATE.stepIndexByGuide
        ),
        isCelebrationOver:
            typeof isCelebrationOver === 'boolean'
                ? isCelebrationOver
                : INITIAL_GUIDES_STATE.isCelebrationOver,
    };
};

export { getInitialGuidesState };
